import { useEffect, useRef, useState } from 'react'
import { loadMapKit } from '@/lib/mapkit'
import type { Airport } from '@/lib/airport-db'
import { bearing, flightProgress, greatCirclePoints, normalizeLon } from '@/lib/geo'
import {
  airportAnnotationElement,
  planeAnnotationElement,
} from '@/lib/flight-map-annotations'

interface FlightMapWebProps {
  departure?: Airport
  arrival?: Airport
  departureDateTime?: Date | string
  arrivalDateTime?: Date | string
  /**
   * Single-point marker shown when no `departure`/`arrival` route is provided
   * (Home with no upcoming flight): centers on the device location.
   */
  location?: { latitude: number; longitude: number; label?: string }
  /** When false the map is a static backdrop (no pan/zoom/rotate). */
  interactive?: boolean
  /**
   * Insets (px) for fitting the route, so it stays clear of content floating
   * over the map (desktop card column, mobile bottom drawer).
   */
  edgePadding?: { top: number; left: number; bottom: number; right: number }
}

/**
 * Desktop web equivalent of the native `FlightMap` (which renders an MKMapView
 * behind the webview via a Capacitor plugin). Renders an Apple MapKit JS map
 * as the home background, with the flight route (or a single location pin).
 */
export function FlightMapWeb({
  departure,
  arrival,
  departureDateTime,
  arrivalDateTime,
  location,
  interactive = true,
  edgePadding,
}: FlightMapWebProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  // The map lives in state (not a ref) so the framing effect below re-runs
  // once the async MapKit JS load completes — otherwise route data that
  // resolves before the map exists is never framed and the map sits at
  // MapKit's default region.
  const [map, setMap] = useState<mapkit.Map | null>(null)
  // Last showItems call, so the resize observer can re-frame.
  const framedRef = useRef<{
    items: Array<mapkit.Annotation | mapkit.Overlay>
    padding: mapkit.Padding
    minimumSpan?: mapkit.CoordinateSpan
  } | null>(null)

  const hasRoute = !!(departure && arrival)

  // Create the map once (and tear it down on unmount).
  useEffect(() => {
    let cancelled = false
    const el = containerRef.current
    if (!el) return

    let map: mapkit.Map | null = null
    loadMapKit()
      .then((mk) => {
        if (cancelled || !containerRef.current) return
        map = new mk.Map(containerRef.current, {
          mapType: mk.Map.MapTypes.Hybrid,
          isRotationEnabled: interactive,
          isZoomEnabled: interactive,
          isScrollEnabled: interactive,
          showsCompass: mk.FeatureVisibility.Hidden,
          showsScale: mk.FeatureVisibility.Hidden,
          showsZoomControl: interactive,
          showsMapTypeControl: false,
          showsUserLocation: false,
          showsUserLocationControl: false,
        })
        setMap(map)
      })
      .catch((e) => console.error('MapKit JS load failed', e))

    return () => {
      cancelled = true
      map?.destroy()
      setMap(null)
    }
    // Recreate only when interactivity changes; route/location handled below.
  }, [interactive])

  // Sync annotations + route + framing whenever the inputs change.
  useEffect(() => {
    const mk = window.mapkit
    if (!map || !mk) return

    map.removeAnnotations(map.annotations ?? [])
    map.removeOverlays(map.overlays ?? [])

    const padding = edgePadding
      ? new mk.Padding(
          edgePadding.top,
          edgePadding.right,
          edgePadding.bottom,
          edgePadding.left,
        )
      : new mk.Padding(96, 80, 96, 80)

    if (hasRoute && departure && arrival) {
      const dep = new mk.Annotation(
        new mk.Coordinate(departure.latitudeDeg, departure.longitudeDeg),
        () => airportAnnotationElement(departure.icaoCode),
      )
      const arr = new mk.Annotation(
        new mk.Coordinate(arrival.latitudeDeg, arrival.longitudeDeg),
        () => airportAnnotationElement(arrival.icaoCode),
      )
      map.addAnnotations([dep, arr])

      const rawPoints = greatCirclePoints(
        departure.latitudeDeg,
        departure.longitudeDeg,
        arrival.latitudeDeg,
        arrival.longitudeDeg,
      )
      const points = rawPoints.map((p) => new mk.Coordinate(p.lat, p.lon))
      // Solid double stroke matching the native MKMapView renderer
      // (FlightMapPlugin.swift): dark-blue outer line with a light-blue core.
      const outer = new mk.PolylineOverlay(points, {
        style: new mk.Style({
          lineWidth: 3,
          strokeColor: '#215CD1',
          strokeOpacity: 0.95,
        }),
      })
      const inner = new mk.PolylineOverlay(points, {
        style: new mk.Style({
          lineWidth: 1.5,
          strokeColor: '#9EC7FF',
          strokeOpacity: 1,
        }),
      })
      map.addOverlay(outer)
      map.addOverlay(inner)

      // Plane glyph at the current flight-progress point along the geodesic,
      // headed toward the next point — mirrors the native plugin's
      // planePosition(on:progress:). Longitude is re-normalized to [-180, 180]
      // since the unwrapped values only matter for polyline continuity.
      const progress = flightProgress(departureDateTime, arrivalDateTime)
      const planeIdx = Math.min(
        rawPoints.length - 1,
        Math.max(0, Math.round((rawPoints.length - 1) * progress)),
      )
      const heading = bearing(
        rawPoints[Math.max(0, planeIdx - 1)],
        rawPoints[Math.min(rawPoints.length - 1, planeIdx + 1)],
      )
      const planeLon = normalizeLon(rawPoints[planeIdx].lon)
      const plane = new mk.Annotation(
        new mk.Coordinate(rawPoints[planeIdx].lat, planeLon),
        () => planeAnnotationElement(heading),
      )
      map.addAnnotation(plane)

      framedRef.current = { items: [dep, arr, outer], padding }
      map.showItems([dep, arr, outer], { animate: false, padding })
    } else if (location) {
      const center = new mk.Coordinate(location.latitude, location.longitude)
      // Same ring+pill annotation as the route endpoints — on iOS the location
      // pin goes through the shared AirportAnnotationView too.
      const marker = new mk.Annotation(center, () =>
        airportAnnotationElement(location.label ?? ''),
      )
      map.addAnnotation(marker)
      // Padded fit (instead of a plain setRegion) so the pin centers in the
      // area left free by content floating over the map; minimumSpan keeps
      // the city-level zoom a single annotation would otherwise lose.
      const minimumSpan = new mk.CoordinateSpan(8, 8)
      framedRef.current = { items: [marker], padding, minimumSpan }
      map.showItems([marker], { animate: false, padding, minimumSpan })
    } else {
      framedRef.current = null
    }
  }, [
    map,
    hasRoute,
    departure?.icaoCode,
    departure?.latitudeDeg,
    departure?.longitudeDeg,
    arrival?.icaoCode,
    arrival?.latitudeDeg,
    arrival?.longitudeDeg,
    departureDateTime,
    arrivalDateTime,
    location?.latitude,
    location?.longitude,
    location?.label,
    edgePadding?.top,
    edgePadding?.left,
    edgePadding?.bottom,
    edgePadding?.right,
  ])

  // Re-frame the route when the container resizes (mirrors the native map's
  // ResizeObserver in FlightMap.tsx), debounced so drag-resizes settle first.
  useEffect(() => {
    const el = containerRef.current
    if (!map || !el) return
    let timer: number | undefined
    const observer = new ResizeObserver(() => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        const framed = framedRef.current
        if (framed) {
          map.showItems(framed.items, {
            animate: false,
            padding: framed.padding,
            minimumSpan: framed.minimumSpan,
          })
        }
      }, 100)
    })
    observer.observe(el)
    return () => {
      window.clearTimeout(timer)
      observer.disconnect()
    }
  }, [map])

  return <div ref={containerRef} className="w-full h-full" />
}
